/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  CellMouseOverEvent,
  ColDef,
  Column,
  ColumnState,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetRowNodeIdFunc,
  GridApi,
  IsGroupOpenByDefaultParams,
  ModelUpdatedEvent,
  RowClickedEvent,
  RowNode
} from 'ag-grid-community';
import {AgGridReact} from 'ag-grid-react';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {AnalyticsIds} from '@ion/core/src/analytics/types';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {CTCEntity, GenericObject} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {DateRangeType} from '@ion/core/src/types/services';
import {smoothScroll} from '@ion/core/src/utils/BrowserUtils';
import {notDeepEqual} from '@ion/core/src/utils/Equality';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import useStopPageMonitor from '@ion/telemetry/src/apm/hooks/useStopPageMonitor';
import {IonSpinner} from '../components/loading/IonSpinner';
import ErrorSnackbar from '../components/snackbars/ErrorSnackbar';
import useErrorPrompt from '../components/snackbars/useErrorPrompt';
import {useIsPhone} from '../hooks/useMedia';
import DragIcon from '../icons/current/DragIcon';
import FilterHoverIcon from '../icons/current/FilterHoverIcon';
import FilterIcon from '../icons/current/FilterIcon';
import HeaderHoverSortIcon from '../icons/current/HeaderHoverSortIcon';
import HeaderSortDownIcon from '../icons/current/HeaderSortDownIcon';
import HeaderSortIcon from '../icons/current/HeaderSortIcon';
import HeaderSortUpIcon from '../icons/current/HeaderSortUpIcon';
import HoverDragIcon from '../icons/current/HoverDragIcon';
import SelectedFilterIcon from '../icons/current/SelectedFilterIcon';
import {Hidden} from '../layout/Hidden';
import useGridColumnGlobalProps from '../props/useGridColumnGlobalProps';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor, dlsSvgColorize} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {GridPageSizeProps} from '../types/types';
import ColumnChooser, {ColumnChooserClient} from './ColumnChooser';
import {
  ExportClient,
  ExportNonPaginatedClient
} from './Exporter';
import {PageConstraintsProps} from './GoToPage';
import GridHeaderCheckbox from './HeaderCheckbox';
import IonGridHeader from './IonGridHeader';
import {
  AG_CLASSES,
  convertFilterModel,
  convertSortModel,
  createErrorMessage,
  FilterModel,
  getDisplayedRows,
  getGridCellStyles,
  getGridDragStyles,
  getGridFilterStyles,
  getGridHeaderStyles,
  getGridSortStyles,
  getPageProxy,
  getSortModel,
  HEADER_HEIGHT,
  ROW_HEIGHT,
  SelectAllCheckbox,
  SortModelType,
  validateRequest
} from './IonGridUtils';
import {IonSpinnerTemplate} from './IonSpinnerTemplate';
import PaginationControls from './PaginationControls';
import {ACTIVE_FILTER_ICON} from './settings';
import {
  ColumnDefinitions,
  useColumnDefinitions
} from './useColumnDefinitions';
import useGridDeselectOnMouseOut from './useGridDeselectOnMouseOut';
import {useGridEventListener} from './useGridEventListener';
import {useGridPageSize} from './useGridPageSize';
import useRowModel from './useRowModel';
import {useStaticGridConfig} from './useStaticGridConfig';

export enum RowModelTypes {
  Infinite = 'infinite',
  ClientSide = 'clientSide',
  ServerSide = 'serverSide'
}
export type AgGridType = GridApi;

export interface ViewSubGroups {
  viewSubGroups?: boolean;
  toggleViewSubGroups?: (view: boolean) => void;
}

export interface ViewOnlyThisAccountProps {
  toggleViewOnlyThisAccount?: (view: boolean) => void;
  viewOnlyThisAccount?: boolean;
  hideOnlyThisAccount?: boolean;
}

export interface ViewInheritableGroupProps {
  toggleViewInheritableGroup?: (view: boolean) => void;
  viewInheritableGroup?: boolean;
  hideInheritableGroup?: boolean;
}

export interface ViewExcludeInheritedGroupProps {
  toggleViewExcludeInheritedGroup?: (view: boolean) => void;
  viewExcludeInheritedGroup?: boolean;
  hideExcludeInheritedGroup?: boolean;
};

export interface GridOperation {
  isPersist?: boolean;
}

export interface BulkHeader {
  onBulkActionsClick?: MouseEventHandler;
  showBulkHeader?: boolean;
}

export interface RowSelectionConfig {
  mode?: 'singleRow' | 'multiRow';
  checkboxes?: boolean;
  headerCheckbox?: boolean;
  selectAll?: 'all' | 'filtered' | 'currentPage';
  hideDisabledCheckboxes?: boolean;
  isRowSelectable?: (node: RowNode) => boolean;
  enableClickSelection?: boolean;
  copySelectedRows?: boolean;
}

// defines the ion grid type definitions ...
export interface IonGridType extends
  ColumnChooserClient,
  SelectAllCheckbox,
  ViewSubGroups,
  ViewOnlyThisAccountProps,
  ViewInheritableGroupProps,
  ViewExcludeInheritedGroupProps,
  GridOperation,
  GridPageSizeProps,
  BulkHeader {
  columnDefinitions: ColDef[] | ColumnDefinitions;
  getSearchParams?: Function;
  rowModelType?: RowModelTypes;
  defaultDefinitions?: ColDef[];
  onPaginationChanged?: (event) => void;
  onColumnsUpdated?: (columns: ColumnState[]) => void;
  checkboxesEnabled?: boolean;
  rowMenusEnabled?: boolean;
  secondaryRowMenusEnabled?: boolean;
  paginationEnabled?: boolean;
  useTopPagination?: boolean;
  topPaginationStyle?: GenericObject;
  searchCondition?: (search?: string) => boolean;
  filterModelConverter?: Function;
  sortModelConverter?: Function;
  loadItems?: (arg: {
    page?: number;
    pageSize?: number;
    sort?: string[] | undefined;
    filter?: GenericObject;
    callback: Function;
  }) => void;
  isLoading?: boolean;
  rowData?: GenericObject[];
  filterModel?: FilterModel;
  totalRows?: number;
  paging?: UsePaginationResult;
  isPrefetching?: boolean;
  onGridReady?: (api: AgGridType) => void;
  onRowDragEnd?: Function;
  isRowSelectable?: Function;
  augmentItems?: Function;
  attributeMappings?: GenericObject;
  onBulkEdit?: (assets: any) => void;
  onBulkDelete?: (assets: any) => void;
  onBulkView?: (assets: any) => void;
  actionMenuItems?: ReactNode;
  onAction?: (assets: any) => void;
  onBulkAcknowledge?: (events: any) => void;
  columnComponents?: {
    [key: string]: ({onClick, data, value}: { onClick?: Function, data?: any, value?: any }) => JSX.Element;
  };
  showCustomPaginationBar?: boolean;
  showHeaderCheckbox?: boolean;
  pageSizes?: number[];
  onPageBack?: (event: MouseEvent<HTMLButtonElement>) => void;
  onPageForward?: (event: MouseEvent<HTMLButtonElement>) => void;
  onManualPaginationChange?: Function;
  setPageSize?: Function;
  refreshRequired?: boolean;
  setRefreshRequired?: (val: boolean) => void;
  preservePageOnRefresh?: boolean;
  pageOffset?: boolean;
  allowSingleDelete?: boolean;
  defaultSort?: SortModelType | {};
  rowHeight?: number;
  defaultGridMinHeight?: number;
  columnProps?: GenericObject;
  ctaProps?: GenericObject;
  secondaryCtaProps?: GenericObject;
  gridOptions?: GenericObject;
  omitHeader?: boolean;
  omitHeaderCTAs?: boolean;
  multilineColumns?: boolean;
  paginationClassName?: string;
  getGridHeight?: Function;
  checkBulkActionDisabledStatus?: Function;
  ref?: any;
  onFilterChanged?: (event: FilterChangedEvent) => void;
  onSortChange?: Function;
  onFilterChange?: Function;
  onModelUpdated?: (event: ModelUpdatedEvent) => void;
  onRowClicked?: (event: RowClickedEvent) => void;
  onCellMouseOver?: (event: CellMouseOverEvent) => void;
  onMouseOut?: Function;
  getRowNodeId?: GetRowNodeIdFunc;
  autoGroupColumnDef?: GenericObject;
  aggFuncs?: GenericObject;
  rowClassRules?: GenericObject;
  getRowStyle?: Function;
  onColumnResized?: Function;
  isDefaultPositionedColumnChooser?: boolean;
  onFirstDataRendered?: (event: FirstDataRenderedEvent) => void;
  hideSubGroups?: boolean;
  singleRowSelection?: boolean;
  autoScrollOnUpdate?: boolean;
  rowBuffer?: number;
  selectedRange?: DateRangeType;
  enableDateFilter?: boolean;
  enableBrowserTooltips?: boolean;
  customScrollTop?: string;
  actionItem?: GenericObject;
  groupDefaultExpanded?: number;
  groupAllowUnbalanced?: boolean;
  isGroupOpenByDefault?: (params: IsGroupOpenByDefaultParams) => boolean;
  showBulkEdit?: boolean;
  disableBulkDelete?: boolean;
  disableBulkEdit?: boolean;
  customSearch?: string;
  searchebleColumns?: GenericObject;
  pageSize?: number;
  setCount?: Function;
  autoClearFocus?: boolean;
  customSelectionHeaderButton?: any;
  showLoadingOverlay?: boolean;
  selectedIds?: any[];
  suppressScrollOnNewData?: boolean;
  loadingOverlayDelay?: number;
  externalHideOverlayControl?: boolean;
  rowPerPageTextTransform?: string;
  showPaginationState?: boolean;
  usePreferedPageSize?: boolean;
  headerHeight?: number,
  pivotHeaderHeight?: number,
  getContextMenuItems?: Function;
  isTotalRowsSelected?: boolean;
  cacheBlockSize?: number;
  pagePreferenceKey?: Preference;
  overlayNoRowsTemplate?: string;
  hideDisabledCheckboxes?: boolean;
  hasCustomDatasource?: boolean;
  disableColumnChooserAutoUpdate?: boolean;
  isDeltaRowDataMode?: boolean;
  enableRangeSelection?: boolean;
  suppressInfiniteLoading?: boolean;
  isToggleSwitchGrid?: boolean;
  hiddenColumns?: GenericObject;
  disableBulkActions?: boolean;
  isClearCellOnPaginationChange?: boolean;
  nestedHeader?: boolean;
  isEnableCellTextSelection?: boolean;
  processCellForClipboard?: (params: any) => any;
  onCellValueChanged?: (e: any) => void;
  showDisabledBulkTooltip?: boolean;
  analyticsId?: AnalyticsIds;
  hasMultiColumnPreferences?: boolean;
  isFilterApplied?: boolean;
  onLoadingChange?: Function;
  autoCompletePageMonitoring?: boolean;
  excludeRows?: CTCEntity[];
  enableExclude?: boolean;
  setExcludedRows?: (rows) => void;
  isDialogControl?: boolean;
  reloadOnVisible?: string[];
  maxBlocksInCache?: number;
  gridError?: Error | string | null;
  listenSortChangeOnMount?: boolean;
  pageConstraintProps?: PageConstraintsProps;
  shouldHandleGridResize?: boolean;
  onRowDoubleClicked?: (event: RowClickedEvent) => void | null;
  suppressContextMenu?: boolean;
  rowSelectionOverrides?: RowSelectionConfig | null;
  bulkDeleteLabel?: string;
}

export type IonGridAPI = {
  getExportParams: (
    viewSubGroups?: boolean,
    enableSelectionsExport?: boolean,
    modularSearchFilter?: GenericObject
  ) => ExportClient;
  getAllDisplayedRowData: () => ExportNonPaginatedClient;
  setPopupParent: (gridIdSelector: string) => void;
  getColumnDefs: () => ColDef[];
  getSelectedColumns: () => Object[];
  onClearFilters: () => void;
  getRowNode?: (id: number) => GenericObject;
  deselectAll?: () => void;
  ensureNodeVisible?: (GenericObject, string) => void;
  onRefresh?: () => void;
  refreshCurrentBlock: () => void;
  refreshInfiniteCache?: () => void;
  updateRowNodes?: (items: GenericObject[], versionCheck?: boolean, options?: GenericObject) => void;
  onToggleColumnChooser?: () => void;
};

const DEFAULT_COLUMN_MIN_WIDTH         = 100;
const CURRENT_MOBILE_COLUMN_MIN_WIDTH  = 160;
const CURRENT_DEFAULT_COLUMN_MIN_WIDTH = 180;
const CURRENT_ROW_HEIGHT               = 44;

const defaultColumnDefinition = {
  editable  : false,
  sortable  : true,
  resizable : false,
  filter    : true,
  minWidth  : DEFAULT_COLUMN_MIN_WIDTH,
  flex      : 1,
  menuTabs  : ['filterMenuTab']
};

// AG-Grid's own `.ag-root-wrapper` (role="presentation", tagged
// `.ag-layout-normal` in normal domLayout) - extracted to a constant since
// it's long enough to force every sibling key in `root` below onto the
// same alignment column otherwise ...
const AG_ROOT_WRAPPER_SELECTOR = '& .ag-layout-normal[role="presentation"]';

// defines the styles used by grid ...
export const useStyles = makeStyles(theme => ({
  root : {
    display                    : 'flex',
    flexDirection              : 'column',
    height                     : '100%',
    flex                       : toggleThemeStyle('unset', '1 1 auto'),
    // legacy keeps its own pre-existing 180px floor, untouched. Current
    // theme intentionally allows the wrapper itself to shrink all the way
    // to 0 - what actually keeps AG-Grid's rows reachable isn't a floor on
    // this wrapper, it's forcing AG-Grid's own nested `.ag-root-wrapper`
    // to `min-height:0` below (current theme only), so IT shrinks to fit
    // instead of clipping against this wrapper's own overflow:hidden ...
    minHeight                  : toggleThemeStyle('180px !important', '0px !important'),
    // this wrapper scrolling on its own (current theme's own 'auto') on
    // top of AG-Grid's own internal `.ag-body-viewport` scroll (which
    // always scrolls once row content exceeds its own enforced minimum
    // height, via applyGridMinimumHeight below) produced two independent
    // scrollbars whenever the grid's available height dropped below that
    // minimum. AG-Grid already provides its own scrolling here - this
    // wrapper just needs to clip, matching legacy's own (already correct)
    // 'hidden' ...
    overflow                   : 'hidden',
    // has no `min-height` rule of its own beyond `height:100%` - as a flex
    // child of this wrapper it falls back to the default `min-height:auto`,
    // which refuses to shrink below its own content's natural size. That
    // fought this wrapper's own min-height/overflow above whenever
    // available height dropped below that natural size, clipping the
    // bottom rows with no way to scroll back to them. Confirmed fixed via
    // live DevTools testing - current theme only, legacy's own layout is
    // untouched ...
    [AG_ROOT_WRAPPER_SELECTOR] : {
      height    : toggleThemeStyle(undefined, '100%'),
      minHeight : toggleThemeStyle(undefined, '0 !important')
    },
    '& .ag-center-cols-container' : {
      minWidth : '100%'
    },
    '& .ag-row-hover.ag-row-hover, .ag-row-hover, .ag-row-selected.ag-row-selected, .ag-row-selected, .ag-row-focus.ag-row-focus, .ag-row-focus' : {
      backgroundColor : `${dlsColor('paleBlue')} !important`,
      backgroundImage : 'none !important',
      '&::before'     : {
        backgroundColor : `${dlsColor('paleBlue')} !important`,
        backgroundImage : 'none !important'
      }
    },
    '& #circle1' : {
      '&::before' : {
        background : `${dlsColor('aquaBlue')} !important`
      }
    },
    '& #circle2' : {
      '&::before' : {
        background : `${dlsColor('tealBlue')} !important`
      }
    },
    '& #circle3' : {
      '&::before' : {
        background : `${dlsColor('frostBlue')} !important`
      }
    },
    '& .ag-checkbox-input-wrapper.ag-checked' : {
      '&::after' : {
        color : `${dlsColor('caBlue')} !important`
      }
    },
    '& .ag-checked' : {
      border          : `2px solid ${dlsColor('caBlue')} !important`,
      backgroundColor : `${dlsColor('caBlue')} !important`
    }
  },
  legacyGridTheme : {
    '& .ag-filter-apply-panel-button[type="submit"]' : {
      color     : `${dlsColor('caBlue')} !important`,
      '&:hover' : {
        color : `${dlsColor('aaBlue')} !important`
      }
    },
    '& .ag-filter-active' : {
      background          : `transparent url("data:image/svg+xml;base64,${dlsSvgColorize(ACTIVE_FILTER_ICON, dlsColor('aaBlue'))}") center/contain no-repeat`,
      '& .ag-icon-filter' : {
        color : `${dlsColor('aaBlue')} !important`
      },
      '& > span' : {
        display : 'none'
      }
    }
  },
  currentGridTheme : {
    ...getGridFilterStyles({
      filterIcon         : <FilterIcon />,
      filterHoverIcon    : <FilterHoverIcon />,
      selectedFilterIcon : <SelectedFilterIcon />
    }),
    ...getGridSortStyles({
      headerSortIcon      : <HeaderSortIcon />,
      headerSortUpIcon    : <HeaderSortUpIcon />,
      headerSortDownIcon  : <HeaderSortDownIcon />,
      headerHoverSortIcon : <HeaderHoverSortIcon />
    }),
    ...getGridDragStyles({
      dragIcon      : <DragIcon />,
      hoverDragIcon : <HoverDragIcon />
    }),
    ...getGridHeaderStyles(),
    ...getGridCellStyles()
  } as any,
  loader : {
    [AG_CLASSES.BODY_LAYOUT] : {
      height : '100vh !important'
    }
  },
  grid : {
    '& [class*=pinned-left-cols], [class*=pinned-right-cols]' : {
      overflow : 'hidden !important'
    },
    '& .ag-header' : {
      top : ({customScrollTop}: {customScrollTop: string}) => customScrollTop || '0px'
    }
  },
  loadMoreSpinner : {
    position       : 'absolute',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    bottom         : '1px'
  },
  pagination : {
    width                          : '100%',
    height                         : toggleThemeStyle('48px', 'auto'),
    minHeight                      : toggleThemeStyle('auto', '48px'),
    flexShrink                     : toggleThemeStyle('unset', '0'),
    [theme.breakpoints.down('sm')] : {
      height          : '104px !important',
      width           : toggleThemeStyle('100% !important', '98% !important'),
      backgroundColor : dlsColor('white')
    }
  },
  legacyPaginationMargin : {
    marginBottom : '40px'
  }
}) as any);

/**
 * @IonGrid
 *
 * Defines the IonGrid that implements the ag-grid library component
 * Expecting the ag-grid styles to be imported into the component file
 * that instantiates this in the app that runs it.
 *
 * @param {Array<any> | object} columnDefinitions column definitions that will default to
 *   desktop if only an array is provided, otherwise pass an object that will contain a
 *   desktop property and mobile property and provide the definitions array to each
 * @param {Function} onPaginationChanged function to be run once the api requests a page change
 * @param {Function} loadItems the function that is
 *   called to load new assets to the grid
 * @param {Function} augmentItems optional Function that will run on the items returned
 *   from loadItems before setting them as row values
 * @param {object} attributeMappings mapping that convert grid fields to sort/filter fields
 *   consumable by ctc
 * @param {Array<number>} pageSizes the options in the select menu to
 *   set the page size
 * @param {boolean} viewSubGroups value that determines if subgroups should be fetched and shown
 * @param {Function} toggleViewSubGroups Hook function that sets the value of viewSubGroups
 * @param {string} actionsColumnId populates the id of the action button column
 * @param {enum} rowModelType determines how the grid loads the rows (server/client side)
 * @param {Function} onPageBack function to run when user
 *   requests to page back
 * @param {Function} onPageForward function to run when user
 *   requests to page forward
 * @param {Function} onBulkEdit function runs when user selects to edit a row
 * @param {Function} onBulkDelete function runs when user selects to delete a row
 * @param {Function} onBulkAcknowledge function runs when user selects to acknowledge multiple rows
 * @param {boolean} paginationEnabled whether or not to allow pagination and show the custom
 *   pagination bar
 * @param {Function} filterModelConverter Custom Filter function
 * @param {Function} sortModelConverter Custom Sort function
 * @param {object} columnComponents this is an object where the key name
 *   references the cellRenderer value for any components to be rendered in a row
 *   and the value of the key is the reference to the component to be rendered
 * @param {boolean} showCustomPaginationBar if true the custom pagination bar is shown, if false,
 *   and pagination is true, then the default ag-grid bar is shown
 * @param {boolean} checkboxesEnabled determines whether the checkboxes will be rendered
 * @param {boolean} rowMenusEnabled determines whether the row menus will be rendered
 * @param {boolean} secondaryRowMenusEnabled determines whether an additional row menu column will be rendered
 * @param {boolean} refreshRequired determines if the grid needs to be refreshed
 * @param {Function} setRefreshRequired sets the state of refreshRequired
 * @param {object} defaultSort default sort that determines what field the grid should be sorted
 *   on when initially loaded or reset
 * @param {Function} onFirstDataRendered callback that is fired once the data has finished rendering in the grid,
 *   helpful because onGridReady will call after initialization not after render.
 * @param {number} rowBuffer set number of rows to render outside of viewable area. By default it is equal to 10.
 *   For multiline rows we need to setup larger value, to render all the available rows at the same time.
 *   helpful because onGridReady will call after initialization not after render.
 * @param {Function} getSelectedRows gets an array of selected rows from the grid.
 * @param {boolean} suppressInfiniteLoading supress overlay on infinite scroll. Defaults to false
 * @param {boolean} isDeltaRowDataMode gridOptions to ensure that grid scroll position does not change on updates.
 * @param {boolean} disableColumnChooserAutoUpdate Stop auto update of grid on column chooser selection. Will update
 * column chooser selections on Done, if set to true.
 *
 * ```typescript
 *
 *  <IonGrid paginationEnabled
 *    checkboxesEnabled
 *    rowMenusEnabled={true}
 *    secondaryRowMenusEnabled={true}
 *    showCustomPaginationBar
 *    loadItems={getAssets}
 *    actionsColumnId = 'assetId',
 *    columnDefinitions={{
 *      desktop: desktopColumnDefs,
 *      mobile: mobileColDefs
 *    }}
 *    filterModelConverter={filterModelConverter}
 *    sortModelConverter={sortModelConverter}
 *    onPaginationChanged={onPaginationChanged}
 *    columnComponents={{
 *      rowMenu   : CellButton,
 *      assetType : AssetTypeCell
 *    }}
 *    pageSizes={PAGE_SIZES}
 *    onBulkEdit={onBulkEdit}
 *    onBulkDelete={onBulkDelete}
 *    augmentItems={augmentItems}
 *    attributeMappings={attributeMappings}
 *    onPageBack={onPageBack}
 *    onPageForward={onPageForward}
 *    refreshRequired={true}
 *    setRefreshRequired={setRefreshRequired}
 *    defaultSort={defaultSort} />
 *
 * ```
 */
export const IonGrid = forwardRef(({
  columnDefinitions,
  defaultDefinitions,
  onPaginationChanged,
  loadItems,
  rowData,
  filterModel,
  totalRows,
  paging,
  augmentItems,
  onGridReady,
  onRowDragEnd,
  isRowSelectable,
  attributeMappings,
  pageSizes,
  toggleViewSubGroups,
  onBulkEdit,
  onBulkDelete,
  onBulkView,
  actionMenuItems,
  onAction,
  onBulkAcknowledge,
  onPageBack,
  onPageForward,
  onManualPaginationChange,
  onColumnChooserSave,
  onColumnChooserClose,
  onColumnsUpdated,
  onRowClicked,
  onCellMouseOver,
  onMouseOut,
  getRowNodeId,
  excludeRows,
  setExcludedRows,
  setGridPageSize,
  cacheBlockSize,
  maxBlocksInCache = 10,
  reloadOnVisible = [],
  disableBulkActions = false,
  isLoading = false,
  isPrefetching = false,
  rowModelType = RowModelTypes.Infinite,
  rowHeight = ROW_HEIGHT,
  defaultGridMinHeight = 150,
  showColumnChooser = false,
  checkboxesEnabled = true,
  rowMenusEnabled = true,
  secondaryRowMenusEnabled = false,
  paginationEnabled = false,
  useTopPagination = false,
  searchCondition,
  filterModelConverter,
  sortModelConverter,
  topPaginationStyle,
  columnComponents = {},
  viewSubGroups = true,
  viewOnlyThisAccount = false,
  viewInheritableGroup = false,
  viewExcludeInheritedGroup = false,
  showCustomPaginationBar = false,
  refreshRequired = false,
  setRefreshRequired,
  preservePageOnRefresh = false,
  pageOffset = false,
  allowSingleDelete = false,
  defaultSort = {},
  columnProps,
  ctaProps = {},
  secondaryCtaProps = {},
  gridOptions,
  onFilterChanged,
  onSortChange,
  onFilterChange,
  onModelUpdated,
  showHeaderCheckbox = true,
  omitHeader = false,
  omitHeaderCTAs = false,
  multilineColumns = false,
  isDefaultPositionedColumnChooser = true,
  paginationClassName,
  getGridHeight,
  checkBulkActionDisabledStatus,
  autoGroupColumnDef,
  aggFuncs,
  rowClassRules,
  getRowStyle,
  onColumnResized,
  onFirstDataRendered,
  customScrollTop,
  actionItem,
  isGroupOpenByDefault,
  getAreTotalRowsSelected,
  customSearch,
  setCount,
  headerHeight = HEADER_HEIGHT,
  pivotHeaderHeight,
  customSelectionHeaderButton,
  getContextMenuItems,
  onBulkActionsClick,
  getSearchParams,
  onLoadingChange,
  processCellForClipboard,
  pageConstraintProps,
  onCellValueChanged,
  hasCustomDatasource = false,
  hideSubGroups = false,
  singleRowSelection = false,
  autoScrollOnUpdate = true,
  rowBuffer,
  selectedRange = null,
  enableExclude = false,
  enableDateFilter = false,
  enableBrowserTooltips = false,
  groupDefaultExpanded = 0,
  groupAllowUnbalanced = true,
  showBulkEdit = true,
  disableBulkEdit = false,
  disableBulkDelete = false,
  searchebleColumns = null,
  pageSize: defaultPageSize = 20,
  autoClearFocus = false,
  showLoadingOverlay = true,
  selectedIds = [],
  suppressScrollOnNewData = false,
  loadingOverlayDelay = 300,
  externalHideOverlayControl = false,
  enableSelectAllCheckbox = false,
  showPaginationState = true,
  rowPerPageTextTransform = 'uppercase',
  usePreferedPageSize = false,
  showBulkHeader = false,
  isTotalRowsSelected = false,
  disableColumnChooserAutoUpdate = false,
  isDeltaRowDataMode= false,
  enableRangeSelection = false,
  suppressInfiniteLoading = false,
  isToggleSwitchGrid = false,
  hiddenColumns = {},
  isPersist = false,
  isClearCellOnPaginationChange = true,
  nestedHeader = false,
  isEnableCellTextSelection = false,
  showDisabledBulkTooltip = false,
  autoCompletePageMonitoring = true,
  hasMultiColumnPreferences = false,
  analyticsId = AnalyticsIds.ionGrid,
  isDialogControl = false,
  listenSortChangeOnMount = false,
  suppressContextMenu = false,
  gridError = null,
  onRowDoubleClicked = null,
  isFilterApplied,
  bulkDeleteLabel,
  overlayNoRowsTemplate = translate('grids:noResults'),
  shouldHandleGridResize = false,
  hideDisabledCheckboxes = true,
  rowSelectionOverrides = null,
  pagePreferenceKey = Preference.gridRowsPerPage
}: IonGridType, ref) => {
  const pageMonitorComplete = useStopPageMonitor();
  const configuration       = useStaticGridConfig(rowBuffer);

  const {isCurrent: isCurrentTheme}                         = useThemeContext();
  const isPhone                                             = useIsPhone();
  const {isClientModel, isInfiniteModel}                    = useRowModel(rowModelType);
  const [fetching, setFetching]                             = useState(isInfiniteModel);
  const classes                                             = useStyles({customScrollTop});
  const [gridApi, setGridApi]                               = useState<AgGridType>(null);
  const [totalResultCount, setTotalResultCount]             = useState(0);
  const [paginationState, setPaginationState]               = useState(null);
  const [selectedRows, setSelectedRows]                     = useState<Array<any>>([]);
  const [isBulkActionDisabled, setBulkActionDisabledStatus] = useState(false);
  const [gridHeight, setGridHeight]                         = useState<number>(0);
  const isMounted                                           = useHasMounted();
  const lastRowClickedRef                                   = useRef(null);
  const [editedRow, setEditedRow]                           = useState(actionItem);
  const [filter, setFilter]                                 = useState(filterModel);
  const areTotalRowsSelected                                = useRef(isTotalRowsSelected);
  const excludeEntities                                     = useRef(excludeRows);
  const preInfiniteModelDependencies                        = useRef(null);
  const [showError, setShowError, onDismiss]                = useErrorPrompt(!!gridError);
  const [errorMessage, setErrorMessage]                     = useState(null);

  const [pageSize, setPageSize, pageSizeRef]                = useGridPageSize({
    usePreferedPageSize, defaultPageSize, setGridPageSize, preferenceKey : pagePreferenceKey
  });

  const paginationRef                                         = useRef<GenericObject>();
  const latestRequestRef                                      = useRef<Function>();
  const restorePageRef                                        = useRef<number>(null);
  const silentRefreshRef                                      = useRef(false);
  const [disablePaginateControls, setDisablePaginateControls] = useState(false);
  const {searchEnabled, orderEnabled}                         = useGridColumnGlobalProps();

  function setAreTotalRowsSelected(value: boolean) {
    getAreTotalRowsSelected?.(value);
    setExcludedRows?.([]);
    areTotalRowsSelected.current = value;
  }

  const columnDefs: ColDef[] = useColumnDefinitions({
    fetching,
    columnDefinitions,
    rowMenusEnabled,
    orderEnabled,
    secondaryRowMenusEnabled,
    gridApi,
    ctaProps,
    secondaryCtaProps,
    pageSize,
    setAreTotalRowsSelected,
    isClientModel,
    checkboxesEnabled    : rowSelectionOverrides ? false : checkboxesEnabled,
    totalRowCount        : totalResultCount,
    enableSelectAll      : enableSelectAllCheckbox,
    areTotalRowsSelected : areTotalRowsSelected.current,
    isPersist            : isPersist
  });
  const [hoverRef] = useGridDeselectOnMouseOut(gridApi, onMouseOut);

  useEffect(() => {
    if (totalRows && totalRows !== totalResultCount) {
      setTotalResultCount(totalRows);
    }
  }, [totalRows]);

  useEffect(() => {
    if (notDeepEqual(filter, filterModel)) {
      setFilter(filterModel);
    }
  }, [filterModel]);

  /**
   * when the grid is ready and component is mounted set the initial
   * popup menu parent and set the row data,
   * otherwise we wont have the grid api yet
   */
  useEffect(() => {
    const hasIMDChange = hasChangeInIMDependencies();
    if (isMounted && gridApi && hasIMDChange) {
      lastRowClickedRef.current = null;
      preInfiniteModelDependencies.current = {
        customSearch,
        selectedRange,
        pageSize,
        viewSubGroups,
        viewOnlyThisAccount,
        viewInheritableGroup,
        viewExcludeInheritedGroup,
        rowModelType,
        searchebleColumns
      };
      if (isInfiniteModel && !hasCustomDatasource) {
        if (preservePageOnRefresh && refreshRequired) {
          restorePageRef.current = getPageProxy(gridApi, totalResultCount).currentPage;
        }
        setRowData(customSearch, searchebleColumns);
        refreshRequired && setRefreshRequired?.(false);
      }
    }
  }, [
    isMounted,
    customSearch,
    selectedRange,
    gridApi,
    refreshRequired,
    viewSubGroups,
    viewOnlyThisAccount,
    viewInheritableGroup,
    viewExcludeInheritedGroup,
    rowModelType,
    isInfiniteModel,
    isClientModel,
    searchebleColumns,
    hasCustomDatasource
  ]);

  useEffect(() => {
    if (gridApi) {
      const hasPageChange = preInfiniteModelDependencies.current?.pageSize !== pageSize;
      if (hasPageChange) {
        // during the page size change, the cache block in grid must be updated before calling the update rows.
        // else this will cause for multiple getRow() call issue from the agGrid
        if (!suppressInfiniteLoading && isInfiniteModel) {
          gridApi.setGridOption('loading', true);
        }
        if (isClientModel) {
          gridApi.setGridOption('paginationPageSize', pageSize);
        }
      }
      preInfiniteModelDependencies.current.pageSize = pageSize;
    }
  }, [gridApi, pageSize]);

  // useEffect responsible for showing loading indicator in grid...
  useEffect(() => {
    if (gridApi) {
      (!externalHideOverlayControl || fetching) && gridApi.setGridOption('loading', fetching);
      if (fetching) {
        !suppressInfiniteLoading ? gridApi.setGridOption('loading', true) : gridApi.hideOverlay();
      } else if (isInfiniteModel && !gridApi.getInfiniteRowCount()) {
        gridApi.showNoRowsOverlay();
      } else {
        !externalHideOverlayControl && gridApi.hideOverlay();
      }
      onLoadingChange?.(fetching);
    }
  }, [isInfiniteModel, fetching, gridApi, suppressInfiniteLoading]);

  // useEffect responsible for handling the client side operations...
  useEffect(() => {
    if (gridApi && isClientModel) {
      if (isLoading) {
        setFetching(true);
      } else {
        setClientSideRowData();
        !loadItems && setFetching(false);
      }
      refreshRequired && setRefreshRequired?.(false);
    }
  }, [gridApi, isClientModel, rowData, isLoading, refreshRequired]);

  /**
   * if grid has multiline columns grid height will be
   * recalculated on each page change.
   */
  useEffect(() => {
    if (multilineColumns) {
      setMultilineColumns();
    }
  }, [gridApi ? getPageProxy(gridApi, totalResultCount).currentPage : 0, multilineColumns]);

  /**
   * if grid has infinite row grid loading overlay will be
   * displayed based on row data.
   */
  useEffect(() => {
    if (gridApi && isInfiniteModel) {
      gridApi.setGridOption('loading', !isLoading);
    }
  }, [gridApi, isInfiniteModel, isLoading]);

  const onRefreshCurrentBlock = useCallback(() => {
    // refresh the current block
    gridApi?.purgeInfiniteCache?.();
    // removing all selection from the grid..
    gridApi?.deselectAll?.();
    setSelectedRows([]);
  }, [gridApi]);

  /**
   * Defines the IonGrid Public API which returns a the current state of the grid
   * which are to be used when exporting and dynamically setting the parent of
   * popup menus.
   */
  useImperativeHandle(ref, () => {
    return {
      /**
       * Defines the export parameters function which returns an object containing
       * the grids current state for use in subsequent exporting of the grid.
       */
      getExportParams : (
        viewSubGroups: boolean = false,
        enableSelectionsExport: boolean = false,
        modularSearchFilter: GenericObject = {}
      ): ExportClient => {
        const sort           = getSortModel(gridApi);
        const filter         = gridApi.getFilterModel();
        const sortModelParam = sort?.length ? sort : [defaultSort];
        return {
          columns : gridApi.getAllDisplayedColumns().map((col: Column) => col.getColDef()),
          sortOn  : sortModelConverter?.(sortModelParam, attributeMappings) ??
            convertSortModel(sort.length ? sort : [defaultSort] as SortModelType[], attributeMappings),
          filterOn : !isEmpty(modularSearchFilter)
            ? modularSearchFilter
            : (filterModelConverter?.(filter, attributeMappings) ?? convertFilterModel(filter, attributeMappings)),
          augmenter        : augmentItems,
          includeSubGroups : viewSubGroups,
          pageSize         : pageSize,
          items            : enableSelectionsExport
            ? gridApi.getSelectedNodes().map((node: RowNode) => node.data)
            : undefined,
          requestValidations : validateRequest({filterModel: filter})
        };
      },
      getAllDisplayedRowData : (): ExportNonPaginatedClient => {
        const items = [];
        gridApi.forEachNodeAfterFilterAndSort(({data}) => items.push(data));
        return {
          columns : gridApi.getAllDisplayedColumns().map((col: Column) => col.getColDef()),
          items
        };
      },
      setPopupParent : (gridIdSelector: string): void => {
        gridApi.setGridOption('popupParent', document.querySelector(`${gridIdSelector} .ag-theme-quartz`));
      },
      getColumnDefs : (): ColDef[] => {
        return columnDefs;
      },
      getSelectedColumns : (): Object[] => {
        return gridApi.getSelectedRows();
      },
      onClearFilters : () => {
        return gridApi?.setFilterModel(null);
      },
      onRefresh            : onRefreshCurrentBlock,
      refreshCurrentBlock  : onRefreshCurrentBlock,
      /**
       * Silently re-requests the loaded blocks in place for live updates -
       * no cache purge, no deselect, no loading overlay. Requires
       * `maxBlocksInCache={1}`: the silent flag clears after the first
       * block settles, so a second block would read it as loud and clear
       * the user's selection.
       */
      refreshInfiniteCache : () => {
        if (gridApi) {
          silentRefreshRef.current = true;
          gridApi.refreshInfiniteCache?.();
        }
      },
      updateRowNodes : (items: GenericObject[], versionCheck = true, options: GenericObject) => {
        if (gridApi) {
          // removing all selection from the local state...
          setSelectedRows([]);
          // unselect the targeted nodes from grid instance...
          gridApi.deselectAll();
          const augmentedItems = augmentItems?.(items, columnProps || options) || items;
          const mappedItems    = idMap(augmentedItems);
          let matchFound       = false;
          gridApi.forEachNode((node: RowNode, index: number) => {
            const {OBJECTID, id, version} = node?.data || {};
            if (OBJECTID || id) {
              const updated = mappedItems[OBJECTID] || mappedItems[id];
              // verifying the incoming data to this function is updated
              // or not by comparing the version of the entity
              const isUpdated = versionCheck ? updated?.version >= version : true;
              if (updated && isUpdated) {
                node.setData(updated);
                matchFound = true;
              }
            }
          });
          if (!matchFound) {
            onRefreshCurrentBlock();
          }
        }
      }
    };
  });

  /**
   * The function will check if the current and previous dependencies
   * for the infinite model grid has changed or not. This function will
   * return true if the current and previous dependencies have changed, else
   * return false.
   */
  function hasChangeInIMDependencies() {
    if (!isEmpty(preInfiniteModelDependencies.current)) {
      const pre = preInfiniteModelDependencies.current;
      if (!refreshRequired) {
        return (
          customSearch !== pre.customSearch ||
          selectedRange !== pre.selectedRange ||
          pageSize !== pre.pageSize ||
          viewSubGroups !== pre.viewSubGroups ||
          viewOnlyThisAccount !== pre.viewOnlyThisAccount ||
          viewInheritableGroup !== pre.viewInheritableGroup ||
          viewExcludeInheritedGroup !== pre.viewExcludeInheritedGroup ||
          rowModelType !== pre.rowModelType ||
          notDeepEqual(searchebleColumns, pre.searchebleColumns)
        );
      }
    }
    return true;
  }

  /**
   *
   * @handleGridReady
   *
   * When grid is ready it begins loading the row items.
   * Additionally will run onGridReady if passed to grid.
   */
  const handleGridReady = useCallback(function(params: GenericObject) {
    if (isCurrentTheme) {
      params?.api?.sizeColumnsToFit();
    }

    if (hoverRef.current) {
      params.api.setGridOption('popupParent', hoverRef.current);
    }

    setGridApi(params.api);
    onGridReady?.(params.api);
  }, []);

  /**
   *
   * @handlePaginationChanged
   *
   * Optionally calls passed onPaginationChanged prop function
   */
  const handlePaginationChanged = useCallback(function(params) {
    lastRowClickedRef.current = null;
    setPaginationState(params);
    onPaginationChanged?.(params);
  }, []);

  /**
   * If the client provides an onColumnsUpdated prop, we create a debounced column change
   * callback which will be invoked whenever the user changes their column selections.
   */
  const onColumnChange = useCallback(() => {
    onColumnsUpdated && (
      debounce(() => onColumnsUpdated(gridApi?.getColumnState()), 300)()
    );
  }, [onColumnsUpdated, gridApi]);

  const onSortChanged = useCallback((e) => {
    lastRowClickedRef.current = null;
    if (onSortChange) {
      if (paging) {
        gridApi.setGridOption('loading', true);
      }
      const sortModel = getSortModel(gridApi);
      const sortModelParam = sortModel.length ? sortModel : [defaultSort] as SortModelType[];
      onSortChange(sortModelConverter?.(sortModelParam, attributeMappings) ??
        convertSortModel(sortModelParam, attributeMappings));
    }
    onColumnChange?.();
  }, [
    paging,
    defaultSort,
    onSortChange,
    gridApi,
    sortModelConverter,
    attributeMappings,
    onColumnChange
  ]);

  useEffect(() => {
    if (gridApi && listenSortChangeOnMount && onSortChange) {
      const sortModel = getSortModel(gridApi);
      const sortModelParam = sortModel.length ? sortModel : [defaultSort] as SortModelType[];
      onSortChange(sortModelConverter?.(sortModelParam, attributeMappings) ??
        convertSortModel(sortModelParam, attributeMappings));
    }
  }, [gridApi]);

  const handleFilterChange = useCallback(function(e) {
    lastRowClickedRef.current = null;
    const filterModel = gridApi?.getFilterModel();
    if (paging && filterModel) {
      onFilterChange(filterModelConverter?.(filterModel, attributeMappings) ??
      convertFilterModel(filterModel, attributeMappings), filterModel);
    }
    setFilter(filterModel as any);
    onFilterChanged?.(e);
  }, [gridApi, onFilterChange, filterModelConverter, convertFilterModel, onFilterChanged]);

  /**
   *
   * @setClientSideRowData
   *
   * Function that is called to set the data source in
   * the grid initially for a client side grid using
   * set Row Data
   */
  function setClientSideRowData() {
    setSelectedRows([]);
    if (!rowData?.length && !totalRows && !!loadItems) {
      setFetching(true);
      try {
        loadItems({
          callback : (result, options) => {
            if (result.error) {
              setFetching(false);
              gridApi.showNoRowsOverlay();
              return gridApi.setGridOption('rowData', undefined);
            }
            const {items, totalCount} = result;
            setClientRows(items, totalCount, options);
            applyGridMinimumHeight(items.length, effectiveRowHeight);
            setFetching(false);
          }
        });
      } catch (err) {
        setFetching(false);
        gridApi.setGridOption('rowData', undefined);
      }
    } else {
      setClientRows(rowData, totalRows);
    }
  }

  const setQueryFilterModel = function(filterModel = filter) {
    if (gridApi) {
      Object.entries(filterModel)?.forEach(async([key, value]) => {
        const instance = await gridApi.getColumnFilterInstance(key);
        instance.setModel(value);
        // [TO-DO]: Currently, this code block is not in use.
        // However, when it is used, we need to verify that
        // the above code activates the filter model for the intended column.
      });
    }
  };

  /**
   *
   * @setClientRows
   *
   * Rows might be passed directly or fetched as part of loadItems.
   * Prevents duplication of setting the row data.
   */
  function setClientRows(items, total, options = {}) {
    setTotalResultCount(total);
    const augmentedItems = augmentItems?.(items, columnProps || options) || items;
    gridApi.setGridOption('rowData', augmentedItems);
    if (selectedIds?.length) {
      gridApi?.forEachNode((node: RowNode, index: number) => {
        const selectNode = selectedIds?.includes(node?.data?.OBJECTID);
        if (selectNode) {
          node?.setSelected(true);
        }
      });
    }
    // setting filter model to the grid after set client side row data
    // adding a small delay, to wait for DOM update.
    const postClientGridSettings = setTimeout(() => {
      if (filter) {
        if (paging) {
          setQueryFilterModel(filter);
        } else {
          gridApi.setFilterModel(filter);
        }
      }
      if (paging && paging.total !== totalRows) {
        paging.setTotal(totalRows);
      }
      setFetching(false);
      if (!getDisplayedRows(gridApi).length) {
        gridApi.showNoRowsOverlay();
      }
      persistGridPosition();
      clearTimeout(postClientGridSettings);
    }, 100);
  }

  function setMultilineColumns() {
    let gridHeight         = 0;
    const nodes            = gridApi?.getRenderedNodes();
    const defaultRowHeight = rowHeight;
    nodes?.forEach(node => {
      if (node?.data && !isNil(node.data?.rowHeight)) {
        const {rowHeight: rHeight} = node.data;
        const rowHeight = Math.max(rHeight, defaultRowHeight);
        gridHeight     += rowHeight;
      }
    });
    setGridHeight(gridHeight);
    getGridHeight?.(gridHeight);
  }

  const getRowHeight = useCallback(function(params) {
    if (multilineColumns && params.data) {
      return params.data.rowHeight;
    }
  }, [multilineColumns]);

  const getRowStyles = useCallback(function(params) {
    const styles = getRowStyle?.(params);
    if (multilineColumns && params.data) {
      const {rowHeight, rowTop} = params.data;
      return {
        ...styles,
        transform : `translateY(${rowTop}px)`,
        height    : rowHeight,
        minHeight : rowHeight
      };
    }
    return styles;
  }, [getRowStyle, multilineColumns]);

  // renamed from `getGridMinimumHeight` - it queries and imperatively
  // mutates DOM style, it doesn't return anything, so a "get" name was
  // misleading. Scoped to this grid's own `hoverRef` container (rather
  // than a document-wide querySelectorAll) so it can never pick up a
  // different IonGrid instance's `.ag-body-viewport` when more than one
  // is mounted on the page ...
  const applyGridMinimumHeight = (length: number, rowHeight: number) => {
    const currentGridInView = hoverRef.current?.querySelector<HTMLElement>('.ag-body-viewport');
    if (currentGridInView) {
      if (length && length < 4) {
        // keeps a few-row grid from stretching to fill all available flex
        // space (which would otherwise leave a large empty gray area below
        // just 1-3 rows). Inherently small (at most 3 rows), so unlike the
        // flat floor below it can't force the body taller than a genuinely
        // short container actually has room for ...
        currentGridInView.style.minHeight = `${length * rowHeight}px`;
      } else if (isCurrentTheme) {
        // no artificial floor here - AG-Grid's own flex layout (its
        // .ag-root-wrapper-body/.ag-root are flex:1 1 auto with their own
        // min-height:0) already sizes the body correctly to whatever space
        // is actually available. A flat defaultGridMinHeight floor here,
        // applied unconditionally regardless of the container's real size,
        // fought that shrinking on every resize - forcing the body taller
        // than the space actually available, clipped by the wrapper's own
        // overflow:hidden, pushing the pagination panel (and the last
        // row(s) behind it) out of reach of any scrolling. Legacy doesn't
        // have the AG_ROOT_WRAPPER_SELECTOR min-height:0 override above,
        // so it still needs this flat floor ...
        currentGridInView.style.minHeight = '0';
      } else {
        currentGridInView.style.minHeight = `${defaultGridMinHeight}px`;
      }
    }
  };

  /**
   *
   * @setRowData
   *
   * Function that is called to set the data source in
   * the grid initially, then teh grid api can handle
   * changes to pagination and page size
   */
  function setRowData(searchVal?: string, searchebleColumns?: GenericObject) {
    const onError = (
      successCallback: Function,
      failCallback: Function,
      {result, message}: {result?: GenericObject, message?: string}
    ) => {
      silentRefreshRef.current = false;
      setFetching(false);
      paginationRef?.current?.setPaginationEnabledState(false);
      setDisablePaginateControls(true);
      (setShowError as Function)(true);
      setErrorMessage(message || createErrorMessage(result));
      successCallback([], 0);
      gridApi.setGridOption('loading', false);
      gridApi.showNoRowsOverlay();
      return failCallback(message || result?.message || '');
    };
    const dataSource = {
      rowCount : null,
      getRows  : ({sortModel, filterModel, startRow, endRow, successCallback, failCallback, ...rest}) => {
        const isSilentRefresh = silentRefreshRef.current;

        if (!isSilentRefresh) {
          !suppressInfiniteLoading && gridApi.setGridOption('loading', true);
          setFetching(true);
          setDisablePaginateControls(true);
          paginationRef?.current?.setPaginationEnabledState(true);
          setSelectedRows([]);
        }
        const page = Math.ceil(startRow / pageSizeRef.current);
        const sortModelParam = sortModel.length ? sortModel : [defaultSort];

        const callback = (result, options) => {
          if (result.error) {
            onError(successCallback, failCallback, {result});
          }
          let {items, totalCount} = result;
          const searchResult = [];
          const checkSearchVal = (searchVal) => {
            searchVal && searchVal === '';
          };
          const validator = searchCondition || checkSearchVal;
          if (validator(searchVal)) {
            searchebleColumns?.map(key => {
              items?.map(item => {
                if (key === 'status' && searchVal === 'ALL') {
                  searchResult.push(item);
                } else if (item[key] !== null && item[key] !== undefined &&
                  item[key]?.toLocaleLowerCase().indexOf(searchVal?.toLocaleLowerCase()) !== -1) {
                  searchResult.push(item);
                }
              });
            });
            items = searchResult?.filter((item, pos) => searchResult?.indexOf(item) === pos) || [];
            totalCount = items.length;
          }
          applyGridMinimumHeight(items?.length, effectiveRowHeight);
          setTotalResultCount(totalCount);
          gridApi.setRowCount(totalCount);
          setCount?.(totalCount < endRow ? totalCount : endRow || pageSizeRef.current);
          const augmentedItems = augmentItems?.(items, columnProps || options) || items;
          let lastRow = -1;
          if (totalCount <= endRow) {
            lastRow = totalCount;
          }
          successCallback(augmentedItems, lastRow);
          const onPostSuccessCallback = setTimeout(() => {
            if (isSilentRefresh) {
              silentRefreshRef.current = false;
            } else {
              setFetching(false);
              paginationRef?.current?.setPaginationEnabledState(false);
              setDisablePaginateControls(false);
            }
            persistGridPosition();
            if (restorePageRef.current) {
              const {currentPage} = getPageProxy(gridApi, totalCount);
              const lastPage      = Math.max(0, Math.ceil(totalCount / pageSizeRef.current) - 1);
              const targetPage    = Math.min(restorePageRef.current, lastPage);
              if (targetPage !== currentPage) {
                gridApi.paginationGoToPage(targetPage);
              }
            }
            restorePageRef.current = null;
            if (!augmentedItems?.length) {
              gridApi.showNoRowsOverlay();
            } else if (isSilentRefresh) {
              gridApi.hideOverlay();
            }
            if (multilineColumns) {
              setMultilineColumns();
            }
            clearTimeout(onPostSuccessCallback);
            if (autoCompletePageMonitoring) {
              pageMonitorComplete();
            }
          }, loadingOverlayDelay);
        };
        // Setting the current request to the ref in order to cancel the request if a new request is made.
        latestRequestRef.current = callback;
        try {
          const {valid, message} = validateRequest({filterModel});
          if (valid) {
            loadItems({
              page     : page + (pageOffset ? 1 : 0),
              pageSize : pageSizeRef.current,
              sort     : sortModelConverter?.(sortModelParam, attributeMappings) ??
              convertSortModel(sortModelParam, attributeMappings),
              filter : filterModelConverter?.(filterModel, attributeMappings) ??
              convertFilterModel(filterModel, attributeMappings),
              callback : (result, options) => {
                // Invoke the callback only if it's the most recent one to prevent race conditions.
                // This ensures that responses from any prior, superseded requests do not trigger a state update,
                // which could result in outdated or incorrect information being displayed to the user.
                if (latestRequestRef.current === callback) {
                  callback(result, options);
                }
              }
            });
          } else {
            onError(successCallback, failCallback, {message});
          }
        } catch (err) {
          setFetching(false);
          paginationRef?.current?.setPaginationEnabledState(false);
          setDisablePaginateControls(false);
          failCallback(err);
        }
      }
    };
    gridApi.setGridOption('datasource', dataSource);
  }

  const onPaginationChange = useCallback(({api, manualTrigger = false}, isSelectionEvent = false) => {
    const selections     = api?.getSelectedRows();
    const excludeRecords = excludeEntities.current;
    setSelectedRows(selections);
    const currentRows = [];
    let excludedRows = [];
    if (!isSelectionEvent) {
      if (!paginationRef?.current?.isDisabled?.current) {
        setDisablePaginateControls(false);
      }
    }
    if (areTotalRowsSelected.current) {
      api.forEachNode((row: { setSelected: Function, data: object }) => {
        if (enableExclude) {
          if (isSelectionEvent) {
            currentRows.push(row.data);
          }
          if (!isSelectionEvent && !excludeRecords?.includes(row.data)) {
            row.setSelected(areTotalRowsSelected.current);
          }
        } else {
          row.setSelected(areTotalRowsSelected.current);
        }
      });
      if (isSelectionEvent && enableExclude) {
        excludedRows = currentRows.filter(item => !selections.includes(item));
        setExcludedRows?.(excludedRows);
        excludeEntities.current = excludedRows;
      }
    } else if (isClearCellOnPaginationChange && isSelectionEvent && !manualTrigger) {
      api.clearFocusedCell();
    }
    if (checkBulkActionDisabledStatus) {
      const isDisabled = checkBulkActionDisabledStatus(selections);
      setBulkActionDisabledStatus(isDisabled);
    }
  }, [checkBulkActionDisabledStatus]);

  /**
   * @onSelectionChanged
   *
   * when any selections are made we get the selected nodes from the
   * grid api and set them in state.
   *
   * the bulk operations are disabled based on the selections and
   * condition if specified.
   */
  function onSelectionChanged(api) {
    onPaginationChange(api, true);
  }

  /**
   * @onClientSideFilterChanged
   *
   * Showing no rows overlay when no rows to show
   * on applying the filter in Client-side Grid.
   */
  function onClientSideFilterChanged({api}) {
    if (isClientModel && !fetching) {
      gridApi.hideOverlay();
      if (getDisplayedRows(gridApi).length === 0) {
        gridApi.showNoRowsOverlay();
      }
    }
    if (isFilterApplied && areTotalRowsSelected) {
      setAreTotalRowsSelected(false);
    }
  }

  /**
   * Get Default context Menu items.
   */
  const defaultContextMenuItems = ['copy', 'copyWithHeaders'];

  /**
   * contextMenuItems method provide the default and custom context Menu items
   * based on the props.
   */
  const contextMenuItems = useCallback(function(params) {
    const items = getContextMenuItems ? getContextMenuItems(params) : defaultContextMenuItems;
    return items;
  }, [getContextMenuItems]);

  useGridEventListener(gridApi, 'selectionChanged', onSelectionChanged);
  useGridEventListener(gridApi, 'paginationChanged', onPaginationChange);
  useGridEventListener(gridApi, 'filterChanged', onClientSideFilterChanged);
  useGridEventListener(gridApi, 'modelUpdated', onClientSideFilterChanged);

  useEffect(() => {
    if (actionItem) {
      setEditedRow(actionItem);
    }
  }, [actionItem]);

  // invoked when the user clicks any row in the grid...
  const onRowClick = useCallback(function(event) {
    onRowClicked?.(event);
    const isUnselect = isEqual(event?.node, lastRowClickedRef.current);
    if (isUnselect && autoClearFocus) {
      gridApi.clearFocusedCell();
    };
    lastRowClickedRef.current = event?.node;
  }, [onRowClicked]);

  /**
   * Function that scrolls the grid to selected row after
   * making any edit to row data.
   */
  const persistGridPosition = function() {
    if (autoScrollOnUpdate && lastRowClickedRef.current && editedRow) {
      const {id, rowTop, oldRowTop} = lastRowClickedRef.current;
      // this must scroll the grid, if the grid has fixed height
      gridApi.ensureIndexVisible(Number(id), rowTop || oldRowTop);
      // this must scroll the page if the grid has auto height
      smoothScroll((rowTop || oldRowTop) - 100, document.getElementById('layout-content')?.parentElement);
      setEditedRow(null);
    } else {
      // Ensure no cells are highlighted since we didn't edit a row.
      gridApi.clearFocusedCell();
    }
  };

  const components = useMemo(() => ({
    headerCheckbox : showHeaderCheckbox ? GridHeaderCheckbox : () => '',
    ...columnComponents
  }), [showHeaderCheckbox, GridHeaderCheckbox, columnComponents, suppressInfiniteLoading]);

  const defaultColDef = useMemo(() => ({
    ...defaultColumnDefinition,
    minWidth : isCurrentTheme && isPhone
      ? CURRENT_MOBILE_COLUMN_MIN_WIDTH
      : isCurrentTheme ? CURRENT_DEFAULT_COLUMN_MIN_WIDTH
        : DEFAULT_COLUMN_MIN_WIDTH
  }), [isCurrentTheme, isPhone]);

  const effectiveRowHeight = useMemo(() => (
    isCurrentTheme && rowHeight === ROW_HEIGHT ? CURRENT_ROW_HEIGHT : rowHeight
  ), [isCurrentTheme, rowHeight]);

  const showExternalSpinner = useMemo(() => {
    return suppressInfiniteLoading && fetching && gridApi?.getDisplayedRowCount?.() > 0;
  }, [fetching, suppressInfiniteLoading, gridApi]);

  /**
   * Returns grid count based on user selection.
   */
  const getRowCount = useCallback(() => {
    if (areTotalRowsSelected.current) {
      return enableExclude ? (totalResultCount - excludeRows?.length) : totalResultCount;
    }
    return selectedRows?.length;
  }, [enableExclude, selectedRows, totalResultCount, excludeRows]);

  const minimumHeightSyncTimeout = useRef<ReturnType<typeof setTimeout>>();

  // re-applies applyGridMinimumHeight's few-row constraint against the
  // current displayed row count - keeps it correct if a resize-triggered
  // re-render also changed which rows are displayed (e.g. via a filter).
  // Kept as its own callback (rather than folded into onGridSizeChanged)
  // so this concern and the no-rows-overlay concern below can each change
  // independently. Debounced - AG-Grid fires its gridSizeChanged event
  // once per ResizeObserver frame with no throttling of its own, and this
  // does a DOM query + style write that only needs to happen once a
  // resize actually settles, not on every frame while it's in progress ...
  const syncGridMinimumHeightOnResize = useCallback((params) => {
    clearTimeout(minimumHeightSyncTimeout.current);
    minimumHeightSyncTimeout.current = setTimeout(() => {
      applyGridMinimumHeight(params.api.getDisplayedRowCount(), effectiveRowHeight);
    }, 150);
  }, [effectiveRowHeight]);

  const onGridSizeChanged = useCallback((params) => {
    syncGridMinimumHeightOnResize(params);
    if (shouldHandleGridResize) {
      const timeout = setTimeout(() => {
        if (params.api.getDisplayedRowCount() === 0) {
          params.api.showNoRowsOverlay();
        }
        clearTimeout(timeout);
      }, 500);
    }
  }, [shouldHandleGridResize, syncGridMinimumHeightOnResize]);

  // creates and returns the grid header, which can be displayed outside of the main
  // grid container (default), or within the container via the `nestedHeader` prop.
  const getHeader = () => {
    if (!omitHeader) {
      return (
        <IonGridHeader hasHeader={gridApi && showBulkHeader}
          count={getRowCount()}
          areTotalRowsSelected={areTotalRowsSelected.current}
          selectedRows={selectedRows}
          omitCTAs={omitHeaderCTAs}
          onBulkActionsClick={showBulkHeader && onBulkActionsClick}
          onBulkView={onBulkView}
          onBulkEdit={onBulkEdit}
          onBulkDelete={onBulkDelete}
          actionMenuItems={actionMenuItems}
          onAction={onAction}
          onBulkAcknowledge={onBulkAcknowledge}
          gridApi={gridApi}
          showBulkEdit={showBulkEdit}
          disableBulkEdit={isBulkActionDisabled || disableBulkEdit}
          disableBulkDelete={isBulkActionDisabled || disableBulkDelete}
          disableBulkActions={disableBulkActions}
          showDisabledBulkTooltip={showDisabledBulkTooltip}
          bulkDeleteLabel={bulkDeleteLabel}
          customSelectionHeaderButton={customSelectionHeaderButton}
          allowSingleDelete={allowSingleDelete} />
      );
    }
  };

  const topPaginationEnabled    = useTopPagination && paginationEnabled;
  const bottomPaginationEnabled = !useTopPagination && paginationEnabled;

  const PaginationControl = (
    <div className={classnames(classes.pagination, paginationClassName, {
      [classes.legacyPaginationMargin] : !isCurrentTheme && !isDialogControl && !topPaginationEnabled
    })}>
      <PaginationControls ref={paginationRef}
        isDialogControl={isDialogControl || topPaginationEnabled}
        gridApi={gridApi}
        loading={fetching}
        disablePaginateControls={disablePaginateControls}
        setDisablePaginateControls={setDisablePaginateControls}
        rowPerPageTextTransform={rowPerPageTextTransform}
        showPaginationState={showPaginationState}
        pageSize={pageSize}
        pageConstraintProps={pageConstraintProps}
        setPageSize={setPageSize}
        paginationState={paginationState}
        totalResultCount={totalResultCount}
        viewSubGroups={viewSubGroups}
        isCurrentTheme={isCurrentTheme}
        paginationStyle={topPaginationEnabled ? topPaginationStyle : {}}
        toggleViewSubGroups={toggleViewSubGroups}
        onManualPaginationChange={onManualPaginationChange}
        hideSubGroups={hideSubGroups}
        isPrefetching={isPrefetching}
        paging={paging}
        pageSizes={pageSizes}
        onPageBack={onPageBack}
        onPageForward={onPageForward} />
    </div>
  );

  const rowSelection = useMemo(() => {
    if (rowSelectionOverrides) {
      return {
        ...rowSelectionOverrides,
        hideDisabledCheckboxes,
        isRowSelectable
      };
    }
    const commonProps = {
      enableClickSelection : false,
      copySelectedRows     : false,
      hideDisabledCheckboxes,
      isRowSelectable
    };
    if (singleRowSelection) {
      return {
        mode : 'singleRow',
        ...commonProps
      };
    }
    return {
      mode           : 'multiRow',
      selectAll      : 'all',
      checkboxes     : false,
      headerCheckbox : false,
      ...commonProps
    };
  }, [
    hideDisabledCheckboxes,
    rowSelectionOverrides,
    singleRowSelection,
    isRowSelectable,
    checkboxesEnabled,
    showHeaderCheckbox
  ]);

  // There needs to be a minimum height for the grid container div in order to ensure
  // that popup menus (which are parented to this div) have sufficient room to display.
  return (
    <>
      <Render.If condition={topPaginationEnabled}>
        {PaginationControl}
      </Render.If>
      {!nestedHeader && getHeader()}
      <div ref={hoverRef}
        data-analytics-id={analyticsId}
        className={classnames(
          'ag-theme-quartz',
          classes.root,
          isCurrentTheme ? classes.currentGridTheme : classes.legacyGridTheme,
          getGridHeight ? getGridHeight(gridHeight) : fetching ? classes.loader : classes.grid
        )}>
        {nestedHeader && getHeader()}
        <AgGridReact onGridReady={handleGridReady}
          onGridSizeChanged={onGridSizeChanged}
          cacheBlockSize={pageSize}
          infiniteInitialRowCount={pageSize}
          pagination={paginationEnabled}
          paginationPageSize={pageSize}
          groupDefaultExpanded={groupDefaultExpanded}
          isGroupOpenByDefault={isGroupOpenByDefault}
          animateRows
          suppressMenuHide
          suppressAggFuncInHeader
          groupAllowUnbalanced={groupAllowUnbalanced}
          getContextMenuItems={contextMenuItems}
          suppressPaginationPanel={showCustomPaginationBar}
          onPaginationChanged={handlePaginationChanged}
          onRowClicked={onRowClick}
          onCellMouseOver={onCellMouseOver}
          getRowId={getRowNodeId}
          maxBlocksInCache={maxBlocksInCache}
          cacheOverflowSize={2}
          rowHeight={effectiveRowHeight}
          rowSelection={rowSelection}
          rowModelType={rowModelType}
          onRowDoubleClicked={onRowDoubleClicked}
          gridOptions={{
            ...gridOptions,
            suppressScrollOnNewData : isDeltaRowDataMode,
            enableRangeHandle       : enableRangeSelection,
            enableRangeSelection,
            processCellForClipboard
          }}
          onColumnMoved={onColumnChange}
          onSortChanged={onSortChanged}
          onColumnResized={onColumnChange}
          onCellValueChanged={onCellValueChanged}
          defaultColDef={defaultColDef as any}
          components={components}
          enableBrowserTooltips={enableBrowserTooltips}
          overlayLoadingTemplate={showLoadingOverlay ? IonSpinnerTemplate : '<div></div>'}
          overlayNoRowsTemplate={overlayNoRowsTemplate}
          localeText={translate('grids:filters', {returnObjects: true})}
          onFilterChanged={handleFilterChange}
          onModelUpdated={onModelUpdated}
          suppressDragLeaveHidesColumns
          autoGroupColumnDef={autoGroupColumnDef}
          aggFuncs={aggFuncs}
          rowClassRules={rowClassRules}
          rowBuffer={configuration.rowBuffer}
          suppressScrollOnNewData={suppressScrollOnNewData}
          headerHeight={headerHeight}
          pivotHeaderHeight={pivotHeaderHeight}
          onFirstDataRendered={onFirstDataRendered}
          suppressContextMenu={suppressContextMenu}
          getRowStyle={getRowStyle || multilineColumns ? getRowStyles : undefined}
          getRowHeight={multilineColumns ? getRowHeight : undefined}
          onRowDragEnd={onRowDragEnd}
          enableCellTextSelection={isEnableCellTextSelection} />
        {showExternalSpinner && (
          <div id={'grid-external-loader'} className={classes.loadMoreSpinner}>
            <IonSpinner />
          </div>
        )}
        <Hidden hide='mobile'>
          {showColumnChooser && isDefaultPositionedColumnChooser && (
            <ColumnChooser columns={isToggleSwitchGrid ? columnDefinitions : columnDefs}
              defaults={defaultDefinitions}
              disableColumnChooserAutoUpdate={disableColumnChooserAutoUpdate}
              onSave={onColumnChooserSave}
              hiddenColumns={hiddenColumns}
              setRefreshRequired={setRefreshRequired}
              reloadOnVisible={reloadOnVisible}
              hasMultiColumnPreferences={hasMultiColumnPreferences}
              customStyles={{hasMultiColumnPreferences}}
              searchEnabled={searchEnabled}
              orderEnabled={orderEnabled}
              onClose={onColumnChooserClose} />
          )}
        </Hidden>
      </div>
      <Render.If condition={bottomPaginationEnabled}>
        {PaginationControl}
      </Render.If>
      <Render.If condition={showError}>
        <ErrorSnackbar message={errorMessage}
          onDismiss={onDismiss as () => void}
          show />
      </Render.If>
    </>
  );
});

export default IonGrid;
